import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, Code2, MapPin, Palette, Play, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import api from '../api';
import ErrorAlert from '../components/ErrorAlert';
import { labelize } from '../components/ProposalItems';
import ComposerSlotCandidates from './ComposerSlotCandidates';

const PRESET_BRIEFS = [
  {
    id: 'family_rain',
    label: 'Family Rain',
    brief: {
      match_party: ['family'],
      kids_ages: [6, 9],
      trip_type_ranked: ['family_time', 'food_finds'],
      weather_handling: 'pack_in_backups',
      pace: 'balanced',
    },
  },
  {
    id: 'couple_evening',
    label: 'Couple Evening',
    brief: {
      match_party: ['couple'],
      trip_type_ranked: ['romantic_escape', 'food_finds'],
      pace: 'relaxed',
    },
  },
  {
    id: 'active_outdoors',
    label: 'Active Outdoors',
    brief: {
      match_party: ['couple'],
      trip_type_ranked: ['adventure', 'nature'],
      pace: 'full',
    },
  },
  {
    id: 'dog_group',
    label: 'Dog Group',
    brief: {
      match_party: ['group'],
      bringing_dog: true,
      trip_type_ranked: ['food_finds', 'classic_muskoka'],
      pace: 'balanced',
    },
  },
];

const THEME_OVERRIDES = [
  { id: '', label: 'Auto' },
  { id: 'date_night', label: 'Date' },
  { id: 'active_outdoors', label: 'Active' },
  { id: 'family_easy', label: 'Family' },
  { id: 'flavour_day', label: 'Food' },
  { id: 'rain_friendly', label: 'Rainy' },
  { id: 'classic_muskoka', label: 'Classic' },
];

function newCompositionSeed() {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return `admin-${Date.now().toString(36)}-${randomPart}`;
}

function prettyJson(value) {
  return JSON.stringify(value || {}, null, 2);
}
function badgeTone(value) {
  if (value === true || value === 'preview_ready' || value === 'allowed' || value === 'eligible') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (value === 'blocked' || value === false || value === 'excluded') {
    return 'border-red-200 bg-red-50 text-red-700';
  }
  return 'border-stone-200 bg-stone-50 text-stone-700';
}
function Badge({ value, children }) {
  const content = children || labelize(value);
  if (!content) return null;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${badgeTone(value)}`}>
      {content}
    </span>
  );
}

function SummaryCard({ title, value, sub, icon: Icon }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-stone-500">{title}</p>
        {Icon && <Icon className="h-4 w-4 text-stone-400" />}
      </div>
      <p className="mt-1 text-2xl font-semibold text-stone-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-stone-500">{sub}</p>}
    </div>
  );
}
function JsonBlock({ value }) {
  return (
    <pre className="max-h-80 overflow-auto rounded-md bg-stone-950 p-3 text-xs leading-5 text-stone-100">
      {prettyJson(value)}
    </pre>
  );
}
function CompactList({ values }) {
  const rows = Array.isArray(values) ? values : [];
  if (!rows.length) return <span className="text-stone-400">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {rows.map(value => <Badge key={value} value={value} />)}
    </div>
  );
}
function CorpusSummary({ corpus }) {
  if (!corpus) return null;
  const distribution = corpus.distance_distribution || {};
  return (
    <section className="space-y-3">
      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard title="Published" value={corpus.total_published_recommendations ?? 0} sub="Recommendation lane" icon={Search} />
        <SummaryCard title="Day-eligible" value={corpus.included_count ?? 0} sub={`${corpus.excluded_count ?? 0} excluded`} icon={ShieldCheck} />
        <SummaryCard title="Distance-ready" value={distribution.distance_ready_card_count ?? 0} sub={`${distribution.pairs_evaluated ?? 0} pairs`} icon={MapPin} />
        <SummaryCard title="Median Pair" value={distribution.median_km == null ? '-' : `${distribution.median_km} km`} sub={distribution.p90_km == null ? '' : `p90 ${distribution.p90_km} km`} />
      </div>
      {Object.keys(corpus.exclusion_reason_counts || {}).length > 0 && (
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <h2 className="font-semibold text-stone-900">Corpus Exclusions</h2>
          <div className="mt-3 grid gap-2 md:grid-cols-3">
            {Object.entries(corpus.exclusion_reason_counts || {}).map(([reason, count]) => (
              <div key={reason} className="flex items-center justify-between rounded-md border border-stone-200 px-3 py-2 text-sm"><span className="text-stone-600">{labelize(reason)}</span><span className="font-semibold text-stone-900">{count}</span></div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
function DayCard({ card, index }) {
  const inputs = card.explanation_inputs || {};
  const staticCopy = inputs.static_copy || {};
  const completeness = card.dossier_completeness || {};
  const retrieval = card.retrieval_debug || {};
  const contributions = retrieval.score_contributions || [];
  return (
    <article className="rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Stop {index + 1}</p>
          <h3 className="mt-1 text-base font-semibold text-stone-900">{card.card_title}</h3>
          <p className="mt-0.5 text-xs text-stone-500">{card.place_master_id} · {card.candidate_id}</p>
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          <Badge value={card.bucket} />
          {card.activity_anchor && <Badge value="anchor">Anchor</Badge>}
        </div>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <div>
          <p className="text-xs font-medium text-stone-500">Pitch</p>
          <p className="mt-1 text-sm text-stone-800">{staticCopy.pitch || '-'}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-stone-500">Knows</p>
          <p className="mt-1 text-sm text-stone-800">{staticCopy.knows || '-'}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-stone-500">Caveat</p>
          <p className="mt-1 text-sm text-stone-800">{staticCopy.caveat || '-'}</p>
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <div>
          <p className="text-xs font-medium text-stone-500">Daypart Slots</p>
          <div className="mt-1"><CompactList values={card.daypart_slots} /></div>
        </div>
        <div>
          <p className="text-xs font-medium text-stone-500">Attribute Kinds</p>
          <div className="mt-1"><CompactList values={inputs.attribute_kinds} /></div>
        </div>
        <div>
          <p className="text-xs font-medium text-stone-500">Dossier</p>
          <p className="mt-1 text-sm text-stone-700">{completeness.attribute_row_count || 0} attributes · {completeness.has_match_signals ? 'signals' : 'no signals'}</p>
        </div>
      </div>
      <div className="mt-3 rounded-md border border-stone-200 bg-stone-50 p-3">
        <p className="text-xs font-medium text-stone-500">Why This Card</p>
        <p className="mt-1 text-sm font-semibold text-stone-900">Score {retrieval.score ?? 0}</p>
        <div className="mt-2 flex flex-wrap gap-1">
          {contributions.length
            ? contributions.map((row, idx) => <Badge key={`${row.reason}:${idx}`}>{row.reason} +{row.score}</Badge>)
            : <span className="text-xs text-stone-400">No positive brief match; selected by availability and distance.</span>}
        </div>
      </div>
    </article>
  );
}
function CandidateTable({ title, rows }) {
  const cards = Array.isArray(rows) ? rows : [];
  if (!cards.length) return null;
  return (
    <section className="overflow-hidden rounded-lg border border-stone-200 bg-white">
      <div className="border-b border-stone-200 bg-stone-50 px-4 py-3">
        <h2 className="font-semibold text-stone-900">{title}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-white text-[11px] font-medium uppercase tracking-wide text-stone-400">
            <tr>
              <th className="px-3 py-2">Card</th>
              <th className="px-3 py-2">Bucket</th>
              <th className="px-3 py-2">Slots</th>
              <th className="px-3 py-2">Dossier</th>
            </tr>
          </thead>
          <tbody>
            {cards.map(card => (
              <tr key={card.candidate_id} className="border-t border-stone-100 align-top hover:bg-stone-50">
                <td className="px-3 py-2">
                  <div className="font-medium text-stone-900">{card.card_title}</div>
                  <div className="text-xs text-stone-500">{card.place_master_id}</div>
                </td>
                <td className="px-3 py-2"><Badge value={card.bucket} /></td>
                <td className="px-3 py-2"><CompactList values={card.daypart_slots} /></td>
                <td className="px-3 py-2 text-xs text-stone-600">
                  {(card.explanation_inputs?.attribute_kinds || []).slice(0, 6).map(labelize).join(', ') || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function PreviewResult({ preview, showDebug, onChangeRowTag, changingRowId }) {
  if (!preview) return null;
  const day = preview.day || {};
  const distance = day.distance_evaluation || {};
  const theme = preview.theme || {};
  const tripTheme = preview.trip_theme || {};
  const themeSub = theme.rethemed
    ? `Source ${labelize(theme.source_theme_id)}; selected by override`
    : (theme.derivation_reasons || []).join('; ');
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard title="Preview" value={labelize(preview.status)} sub={`${(day.row_selected_cards || day.cards || []).length} row selected`} icon={preview.status === 'preview_ready' ? CheckCircle2 : AlertTriangle} />
        <SummaryCard title="Trip Theme" value={tripTheme.label || '-'} sub={(tripTheme.derivation_reasons || []).join('; ')} />
        <SummaryCard title="Source Theme" value={theme.label || '-'} sub={themeSub} />
        <SummaryCard title="Distance" value={labelize(distance.rule)} sub={distance.max_distance_km == null ? distance.reason : `${distance.max_distance_km} km max`} />
      </div>

      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <h2 className="font-semibold text-stone-900">Selected Day</h2>
        <p className="mt-1 text-sm text-stone-500">Light day-card preview. Guest prose generation comes after this selection/debug path is stable.</p>
        {day.shape_requirement && (
          <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            <span className="font-medium">{labelize(day.shape_requirement.rule)}</span>
            {' '}
            {day.shape_requirement.satisfied ? 'satisfied' : 'not satisfied'}
            {day.shape_requirement.reason ? ` · ${day.shape_requirement.reason}` : ''}
          </div>
        )}
        <div className="mt-4 space-y-3">
          {(day.cards || []).map((card, index) => <DayCard key={card.candidate_id} card={card} index={index} />)}
          {!(day.cards || []).length && <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{distance.reason || 'No coherent day could be composed.'}</p>}
        </div>
      </section>

      <ComposerSlotCandidates
        slots={day.slots}
        onChangeRowTag={onChangeRowTag}
        changingRowId={changingRowId}
      />

      {showDebug && (
        <>
          <section className="rounded-lg border border-stone-200 bg-white p-4">
            <h2 className="font-semibold text-stone-900">Distance Debug</h2>
            <div className="mt-3"><JsonBlock value={distance} /></div>
          </section>
          <CandidateTable title="Not Selected Candidates" rows={preview.debug?.not_selected_candidates} />
          <section className="rounded-lg border border-stone-200 bg-white p-4">
            <h2 className="font-semibold text-stone-900">Raw Preview Payload</h2>
            <div className="mt-3"><JsonBlock value={preview} /></div>
          </section>
        </>
      )}
    </div>
  );
}

export default function ComposerPreviewView() {
  const [area, setArea] = useState('Bracebridge');
  const [areaInput, setAreaInput] = useState('Bracebridge');
  const [briefText, setBriefText] = useState(prettyJson(PRESET_BRIEFS[0].brief));
  const [tripContextText, setTripContextText] = useState(prettyJson({}));
  const [compositionSeed, setCompositionSeed] = useState(newCompositionSeed);
  const [themeOverride, setThemeOverride] = useState('');
  const [rowTagOverrides, setRowTagOverrides] = useState({});
  const [changingRowId, setChangingRowId] = useState('');
  const [maxCards, setMaxCards] = useState(4);
  const [candidateLimit, setCandidateLimit] = useState(24);
  const [corpus, setCorpus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingCorpus, setLoadingCorpus] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showDebug, setShowDebug] = useState(true);
  const [error, setError] = useState('');

  const briefParseError = useMemo(() => {
    try {
      JSON.parse(briefText || '{}');
      return '';
    } catch (err) {
      return err.message;
    }
  }, [briefText]);
  const tripContextParseError = useMemo(() => {
    try {
      JSON.parse(tripContextText || '{}');
      return '';
    } catch (err) {
      return err.message;
    }
  }, [tripContextText]);

  const loadCorpus = async nextArea => {
    const scopedArea = (nextArea || area || 'Bracebridge').trim();
    setLoadingCorpus(true);
    setError('');
    try {
      setCorpus(await api.getComposerCorpus({ area: scopedArea, include_pairs: 'false', pair_limit: '0' }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCorpus(false);
    }
  };

  const runPreview = async (nextRowTagOverrides = rowTagOverrides) => {
    if (briefParseError || tripContextParseError) return;
    setLoadingPreview(true);
    setError('');
    try {
      const brief = JSON.parse(briefText || '{}');
      const tripContext = JSON.parse(tripContextText || '{}');
      setPreview(await api.runComposerPreview({
        area,
        brief: { ...brief, area: brief.area || area },
        max_cards: Number(maxCards),
        candidate_limit: Number(candidateLimit),
        trip_context: tripContext,
        composition_seed: compositionSeed,
        theme_override: themeOverride || null,
        row_tag_overrides: nextRowTagOverrides,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const changeRowTag = async (row, value) => {
    const rowId = row?.row_id;
    if (!rowId || !value) return;
    const nextOverrides = { ...rowTagOverrides, [rowId]: value };
    setRowTagOverrides(nextOverrides);
    setChangingRowId(rowId);
    try {
      await runPreview(nextOverrides);
    } finally {
      setChangingRowId('');
    }
  };

  useEffect(() => { loadCorpus('Bracebridge'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4 text-stone-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Composer Preview</h1>
          <p className="text-sm text-stone-500">Admin-only Composer v1 test surface over published, source-safe, trusted-coordinate cards.</p>
        </div>
        <form
          onSubmit={event => {
            event.preventDefault();
            const nextArea = areaInput.trim() || 'Bracebridge';
            setArea(nextArea);
            loadCorpus(nextArea);
          }}
          className="flex items-center gap-2"
        >
          <div className="flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-stone-400" />
            <input value={areaInput} onChange={event => setAreaInput(event.target.value)} className="w-44 text-sm outline-none" />
          </div>
          <button type="submit" className="rounded-lg bg-stone-900 px-3 py-2 text-sm font-medium text-white hover:bg-stone-800">Load Corpus</button>
          <button type="button" onClick={() => loadCorpus(area)} className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </form>
      </div>

      {error && <ErrorAlert error={error} onClose={() => setError('')} />}
      {loadingCorpus && <p className="rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm text-stone-500">Loading Composer corpus...</p>}
      <CorpusSummary corpus={corpus} />

      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-stone-900">Brief</h2>
            <p className="text-sm text-stone-500">Choose a launch brief or edit the JSON directly.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESET_BRIEFS.map(preset => (
              <button
                type="button"
                key={preset.id}
                onClick={() => {
                  setBriefText(prettyJson(preset.brief));
                  setRowTagOverrides({});
                }}
                className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div className="space-y-3">
            <textarea
              rows={10}
              value={briefText}
              onChange={event => {
                setBriefText(event.target.value);
                setRowTagOverrides({});
              }}
              className={`w-full rounded-lg border px-3 py-2 font-mono text-xs leading-5 outline-none focus:ring-1 ${briefParseError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-stone-300 focus:border-stone-900 focus:ring-stone-900'}`}
            />
            <label className="block text-xs font-medium uppercase tracking-wide text-stone-500">
              Trip Context
              <textarea
                rows={5}
                value={tripContextText}
                onChange={event => setTripContextText(event.target.value)}
                className={`mt-1 w-full rounded-lg border px-3 py-2 font-mono text-xs leading-5 outline-none focus:ring-1 ${tripContextParseError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-stone-300 focus:border-stone-900 focus:ring-stone-900'}`}
              />
            </label>
          </div>
          <div className="space-y-3">
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase text-stone-500">
                <Palette className="h-3.5 w-3.5" />
                Theme
              </div>
              <div className="grid grid-cols-2 gap-1 rounded-lg border border-stone-200 bg-stone-50 p-1">
                {THEME_OVERRIDES.map(theme => (
                  <button
                    type="button"
                    key={theme.id || 'auto'}
                    onClick={() => setThemeOverride(theme.id)}
                    className={`rounded-md px-2 py-1.5 text-xs font-medium ${themeOverride === theme.id ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-white'}`}
                  >
                    {theme.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="block text-xs font-medium uppercase tracking-wide text-stone-500">
              Seed
              <div className="mt-1 flex gap-2">
                <input value={compositionSeed} onChange={event => setCompositionSeed(event.target.value)} className="min-w-0 flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900" />
                <button type="button" onClick={() => setCompositionSeed(newCompositionSeed())} title="New seed" aria-label="New seed" className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50">
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </label>
            <label className="block text-xs font-medium uppercase tracking-wide text-stone-500">
              Max Cards
              <input type="number" min="1" max="4" value={maxCards} onChange={event => setMaxCards(event.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900" />
            </label>
            <label className="block text-xs font-medium uppercase tracking-wide text-stone-500">
              Candidate Limit
              <input type="number" min="1" max="50" value={candidateLimit} onChange={event => setCandidateLimit(event.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900" />
            </label>
            <button
              type="button"
              onClick={() => runPreview()}
              disabled={loadingPreview || Boolean(briefParseError || tripContextParseError)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
            >
              <Play className="h-4 w-4" />
              {loadingPreview ? 'Running...' : 'Run Preview'}
            </button>
            <button
              type="button"
              onClick={() => setShowDebug(value => !value)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
            >
              {showDebug ? <ChevronDown className="h-4 w-4" /> : <Code2 className="h-4 w-4" />}
              {showDebug ? 'Hide Debug' : 'Show Debug'}
            </button>
            {briefParseError && <p className="text-xs text-red-600">{briefParseError}</p>}
            {tripContextParseError && <p className="text-xs text-red-600">{tripContextParseError}</p>}
          </div>
        </div>
      </section>

      <PreviewResult
        preview={preview}
        showDebug={showDebug}
        onChangeRowTag={changeRowTag}
        changingRowId={changingRowId}
      />
    </div>
  );
}
